import React, { useEffect, useRef, useState } from 'react';
 
function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
 
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setPrefersReducedMotion(mediaQuery.matches);
 
    onChange();
 
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', onChange);
      return () => mediaQuery.removeEventListener('change', onChange);
    }
 
    mediaQuery.addListener(onChange);
    return () => mediaQuery.removeListener(onChange);
  }, []);
 
  return prefersReducedMotion;
}
 
function IntroMedia() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [videoFailed, setVideoFailed] = useState(false);
  const [variant, setVariant] = useState('1080');
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef(null);

  const poster = `${process.env.PUBLIC_URL}/assets/intro-poster.webp`;
  const mp4_720 = `${process.env.PUBLIC_URL}/assets/intro-720.mp4`;
  const mp4_1080 = `${process.env.PUBLIC_URL}/assets/intro-1080.mp4`;

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 640px)');

    const readConnectionHints = () => {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const saveData = Boolean(connection && connection.saveData);
      const effectiveType = connection && connection.effectiveType ? String(connection.effectiveType) : '';
      const slowNetwork = ['slow-2g', '2g', '3g'].includes(effectiveType);
      return { saveData, slowNetwork };
    };

    const chooseVariant = () => {
      const { saveData, slowNetwork } = readConnectionHints();
      const shouldPrefer720 = mediaQuery.matches || saveData || slowNetwork;
      setVariant(shouldPrefer720 ? '720' : '1080');
    };

    chooseVariant();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', chooseVariant);
      return () => mediaQuery.removeEventListener('change', chooseVariant);
    }

    mediaQuery.addListener(chooseVariant);
    return () => mediaQuery.removeListener(chooseVariant);
  }, []);
 
  if (prefersReducedMotion || videoFailed) {
    return (
      <img
        className="intro-fallback"
        src={poster}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
      />
    );
  }

  const togglePlayback = async () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (videoEl.paused) {
      try {
        await videoEl.play();
        setIsPlaying(true);
      } catch {
        // ignore autoplay/promise issues; keep UI stable
      }
    } else {
      videoEl.pause();
      setIsPlaying(false);
    }
  };

  return (
    <div className="intro-media">
      <div className="intro-glow" aria-hidden="true" />
      <video
        className="intro-video"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster={poster}
        onError={() => setVideoFailed(true)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        ref={videoRef}
      >
        <source src={variant === '720' ? mp4_720 : mp4_1080} type="video/mp4" />
      </video>
      <button
        className="intro-control"
        type="button"
        onClick={togglePlayback}
        aria-label={isPlaying ? 'Pause animation' : 'Play animation'}
        aria-pressed={!isPlaying}
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <rect x="6.5" y="5" width="4.5" height="14" rx="1.2" fill="currentColor" />
            <rect x="13" y="5" width="4.5" height="14" rx="1.2" fill="currentColor" />
          </svg>
        ) : (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M8.5 6.7v10.6c0 .9 1 1.4 1.8.9l9.1-5.3c.8-.5.8-1.6 0-2.1l-9.1-5.3c-.8-.5-1.8 0-1.8.9Z" fill="currentColor" />
          </svg>
        )}
      </button>
    </div>
  );
}

export default IntroMedia;
